import { useEffect, useRef, useState } from 'react';
import {
  apiErrorText,
  useDeleteCeoDebtInstrumentMutation,
  useGetCeoDebtDetailQuery,
  useMarkCeoDebtSchedulePaidMutation,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import { INSTRUMENT_TYPE_LABELS } from '../ceoFormat';
import { useTreasuryWrite } from '../treasuryAccess';
import { DebtDetailInfoCards } from './DebtDetailInfoCards';
import { DebtDetailKpiGrid } from './DebtDetailKpiGrid';
import { DebtDetailScheduleTable } from './DebtDetailScheduleTable';
import {
  ConfirmActions,
  ConfirmText,
  DeleteButton,
  Dialog,
  ErrorAlert,
  Header,
  HeaderActionsRow,
  HeaderButtons,
  Overlay,
  SubTitle,
  Title,
  TitleBlock,
} from './styles';
import type { DebtDetailModalProps } from './types';

export function DebtDetailModalView({
  instrumentId,
  onClose,
  editable = false,
}: DebtDetailModalProps) {
  const can_write = useTreasuryWrite(editable);
  const { data, isLoading, isError, refetch } = useGetCeoDebtDetailQuery(instrumentId);
  const [markPaid, { isLoading: isMarking }] = useMarkCeoDebtSchedulePaidMutation();
  const [deleteDebt, { isLoading: isDeleting }] = useDeleteCeoDebtInstrumentMutation();
  const [paidRef, setPaidRef] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [markingItemId, setMarkingItemId] = useState<string | null>(null);
  const [askDelete, setAskDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Пока открыт вопрос об удалении, Esc закрывает только его, а не карточку.
    if (askDelete) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, askDelete]);

  useEffect(() => {
    if (data) {
      dialogRef.current?.focus();
    }
  }, [data]);

  if (isLoading) {
    return (
      <Overlay onClick={onClose}>
        <Dialog
          role="dialog"
          aria-modal="true"
          aria-labelledby="debt-detail-loading"
          onClick={(e) => e.stopPropagation()}
        >
          <Title id="debt-detail-loading">Загрузка паспорта кредитного / лизингового договора…</Title>
        </Dialog>
      </Overlay>
    );
  }

  if (isError || !data) {
    return (
      <Overlay onClick={onClose}>
        <Dialog
          role="dialog"
          aria-modal="true"
          aria-labelledby="debt-detail-error"
          onClick={(e) => e.stopPropagation()}
        >
          <Header>
            <Title id="debt-detail-error">Не удалось загрузить паспорт договора</Title>
            <HeaderActionsRow>
              <Button $size="sm" $variant="secondary" onClick={() => refetch()}>
                Повторить
              </Button>
              <Button $variant="ghost" onClick={onClose}>
                Закрыть ✕
              </Button>
            </HeaderActionsRow>
          </Header>
        </Dialog>
      </Overlay>
    );
  }

  const inst = data.instrument;
  const isLeasing = inst.instrument_type === 'leasing';

  async function handleMarkPaid(itemId: string, amount: number) {
    try {
      setActionError(null);
      setMarkingItemId(itemId);
      await markPaid({
        item_id: itemId,
        paid_amount: amount,
        payment_reference: paidRef.trim() || 'Исполнено по банковской выписке',
      }).unwrap();
      setPaidRef('');
      refetch();
    } catch (err: any) {
      const msg =
        err?.data?.message || err?.error || err?.message || 'Не удалось отметить оплату транша';
      setActionError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setMarkingItemId(null);
    }
  }

  function closeDeleteQuestion() {
    if (isDeleting) return;
    setAskDelete(false);
    setDeleteError(null);
  }

  async function handleDelete() {
    try {
      setDeleteError(null);
      await deleteDebt(instrumentId).unwrap();
      setAskDelete(false);
      onClose();
    } catch (err) {
      setDeleteError(apiErrorText(err, 'Не удалось удалить договор.'));
    }
  }

  return (
    <Overlay onClick={onClose}>
      <Dialog
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="debt-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <TitleBlock>
            <SubTitle>
              <Badge $tone={isLeasing ? 'info' : 'warning'}>
                {INSTRUMENT_TYPE_LABELS[inst.instrument_type] ?? inst.instrument_type}
              </Badge>
              <span>Договор № {inst.contract_number}</span>
              <span>·</span>
              <span>{inst.creditor_name}</span>
              {inst.creditor_inn ? <span>(ИНН {inst.creditor_inn})</span> : null}
            </SubTitle>
            <Title id="debt-detail-title">{inst.title}</Title>
          </TitleBlock>
          <HeaderButtons>
            {can_write && (
              <DeleteButton $variant="secondary" type="button" onClick={() => setAskDelete(true)}>
                Удалить договор
              </DeleteButton>
            )}
            <Button $variant="ghost" onClick={onClose}>
              Закрыть ✕
            </Button>
          </HeaderButtons>
        </Header>

        {actionError && <ErrorAlert>{actionError}</ErrorAlert>}

        <DebtDetailKpiGrid
          inst={inst}
          recalculatedEffectiveRatePct={data.recalculated_effective_rate_pct}
          cbrKeyRatePct={data.cbr_key_rate_pct}
          totalPaidToDate={data.total_paid_to_date}
          remainingToPay={data.remaining_to_pay}
        />

        <DebtDetailInfoCards inst={inst} />

        <DebtDetailScheduleTable
          schedule={data.schedule}
          totalScheduledPrincipal={data.total_scheduled_principal}
          totalScheduledInterest={data.total_scheduled_interest}
          totalScheduledFees={data.total_scheduled_fees}
          can_write={can_write}
          paidRef={paidRef}
          onPaidRefChange={setPaidRef}
          onMarkPaid={handleMarkPaid}
          markingItemId={markingItemId}
          isMarking={isMarking}
        />

        {/* Внутри окна карточки: поверх неё и без закрытия карточки щелчком по подложке. */}
        <Modal
          open={askDelete}
          title={`Удалить договор № ${inst.contract_number}?`}
          onClose={closeDeleteQuestion}
        >
          <ConfirmText>
            Договор и его график платежей будут удалены. Платежи по выпискам останутся, но
            отвяжутся от договора.
          </ConfirmText>
          {deleteError && <ErrorAlert>{deleteError}</ErrorAlert>}
          <ConfirmActions>
            <Button
              $variant="ghost"
              type="button"
              disabled={isDeleting}
              onClick={closeDeleteQuestion}
            >
              Отмена
            </Button>
            <Button $variant="danger" type="button" disabled={isDeleting} onClick={handleDelete}>
              {isDeleting ? 'Удаляю…' : 'Удалить'}
            </Button>
          </ConfirmActions>
        </Modal>
      </Dialog>
    </Overlay>
  );
}
